import Block from '@/components/Block'
import Button from '@/components/Button'
import { Stack } from '@/components/Layout'
import Text from '@/components/Text'
import { coreLayoutContainer, coreLayoutSection, rbp } from '@/constants/measured-scope'
import { useFeatureToggle } from '@/context/featureToggles'
import { eventsTracker, UIElementNames } from '@/lib/eventsTracker'
import { PartnershipBannerContent } from '@/shared/api-generated-types'
import { useMediaQuery } from '@react-hookz/web'
import { hasCookie, setCookie } from 'cookies-next'
import dayjs from 'dayjs'
import { useRouter } from 'next/router'
import * as styles from './style'

const excludedPartners = ['aetna', 'silversneakers']

export interface Props {
  id: string
  content: PartnershipBannerContent
}

const bannerOnClick = (event: React.MouseEvent<HTMLElement>): void => {
  eventsTracker.handleOnClickWithDistinctionAndTrackEvent(
    UIElementNames.LINK_PARTNER_AD_BANNER,
    event.currentTarget.getAttribute('data-distinction'),
  )
}

const PartnershipBannerBlock = ({ content, id }: Props): JSX.Element | null => {
  const router = useRouter()
  const partnerSlug = router.query?.slug
  const showPartnershipBanner = useFeatureToggle('tivity_oec_banner')
  const cookieName = `partnership-ad-banner-${content.partnerName}`

  // Breakpoint helpers
  const isDesktop = useMediaQuery(`only screen and (min-width: ${rbp.desktop})`)
  const isTablet = useMediaQuery(`only screen and (min-width: ${rbp.tablet})`)
  const isSmallTablet = useMediaQuery(`only screen and (min-width: ${rbp.smallTablet})`)

  if (
    hasCookie(cookieName) ||
    !showPartnershipBanner ||
    (typeof partnerSlug === 'string' && excludedPartners.indexOf(partnerSlug) !== -1)
  )
    return null

  // We need to calculate correct image and text padding
  let backgroundImage = content.imageUrls.mobile
  if (isDesktop) {
    backgroundImage = content.imageUrls.desktop
  } else if (isTablet) {
    backgroundImage = content.imageUrls.tablet
  } else if (isSmallTablet) {
    backgroundImage = content.imageUrls.smallTablet
  }

  return (
    <Block key={id} id={id} className={coreLayoutSection} spacing="sm">
      <a
        href={content.link}
        data-distinction={content.partnerName}
        onClick={(event) => {
          bannerOnClick(event)
          setCookie(cookieName, 1, { expires: dayjs().add(30, 'days').toDate() })
        }}
        target="_blank"
        rel="noopener"
      >
        <div
          className={`${coreLayoutContainer} ${styles.root}`}
          style={{ backgroundImage: `url("${backgroundImage}")` }}
        >
          <Stack className={styles.container} y alignItems="flex-start" justifyContent="flex-start">
            <img alt={content.label} src={content.logoUrl} className={styles.logo} />
            <Text tag="h2" weight="lg" className={styles.title}>
              {content.title}
            </Text>
            <Text weight="sm" className={styles.description}>
              {content.description}
            </Text>
            <Button
              theme="accent"
              stretch={!isSmallTablet}
              label={content.label}
              className={styles.button}
              size="md"
              component="link"
              href={content.link}
              target="_blnk"
            />
          </Stack>
        </div>
      </a>
    </Block>
  )
}
export default PartnershipBannerBlock
